import React, { useState, useEffect } from 'react';
import { ADMIN_API_ENDPOINTS, FRONTEND_URL, SERVER_URL } from '../config/api';
import { getAdminToken } from '../services/apiService';
import Toast from './Toast';
import ConfirmDialog from './ConfirmDialog';

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null); // 跟踪哪个语言的图片正在上传 ('zh' or 'en')


  // 获取完整图片URL的辅助函数
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    // 如果已经是完整URL,直接返回
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    // 如果是相对路径,添加SERVER_URL前缀
    return `${SERVER_URL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const faqTemplate = {
    question: { en: '', zh: '' },
    answer: { en: '', zh: '' },
    image: { en: '', zh: '' },
    sort_order: 0,
    status: 'published',
  };

  // 处理图片上传
  const handleImageUpload = async (e, lang) => {
    const file = e.target.files[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      showToast('请选择图片文件', 'error');
      e.target.value = '';
      return;
    }

    // 验证文件大小 (5MB)
    if (file.size > 30 * 1024 * 1024) {
      showToast('图片大小不能超过30MB', 'error');
      e.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    setUploadingImage(lang);
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.FAQS.UPLOAD_IMAGE, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        // 使用函数式更新确保获取最新状态
        setEditingFaq((prev) => ({
          ...prev,
          image: { ...prev.image, [lang]: data.imageUrl },
          // 添加时间戳强制重新渲染
          _imageUpdateTimestamp: Date.now(),
        }));
        showToast('图片上传成功', 'success');
      } else {
        showToast(data.message || '上传失败', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast('上传失败', 'error');
    } finally {
      setUploadingImage(null);
      // 清空文件输入,允许重新上传相同文件
      e.target.value = '';
    }
  };

  // 显示Toast
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // 获取FAQ列表
  const fetchFaqs = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(ADMIN_API_ENDPOINTS.FAQS.LIST, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFaqs(data.faqs || []);
      } else {
        showToast('获取FAQ列表失败', 'error');
      }
    } catch (error) {
      console.error('Fetch FAQs error:', error);
      showToast('获取FAQ列表失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // 新建FAQ
  const handleCreate = () => {
    setEditingFaq({ ...faqTemplate });
    setShowDialog(true);
  };

  // 编辑FAQ
  const handleEdit = (faq) => {
    setEditingFaq({ ...faq });
    setShowDialog(true);
  };

  // 保存FAQ
  const handleSave = async () => {
    try {
      const token = getAdminToken();
      const url = editingFaq.id
        ? ADMIN_API_ENDPOINTS.FAQS.BY_ID(editingFaq.id)
        : ADMIN_API_ENDPOINTS.FAQS.LIST;

      const method = editingFaq.id ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editingFaq),
      });

      if (response.ok) {
        setShowDialog(false);
        setEditingFaq(null);
        fetchFaqs();
        showToast(editingFaq.id ? 'FAQ已更新' : 'FAQ已创建', 'success');
      } else {
        showToast('保存失败', 'error');
      }
    } catch (error) {
      console.error('Save FAQ error:', error);
      showToast('保存失败', 'error');
    }
  };

  // 删除FAQ
  const handleDelete = async (id) => {
    setConfirmDialog({
      message: '确定要删除此FAQ吗？此操作不可撤销。',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = getAdminToken();
          const response = await fetch(ADMIN_API_ENDPOINTS.FAQS.BY_ID(id), {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            fetchFaqs();
            showToast('FAQ已删除', 'success');
          } else {
            showToast('删除失败', 'error');
          }
        } catch (error) {
          console.error('Delete FAQ error:', error);
          showToast('删除失败', 'error');
        }
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  // 切换发布状态
  const handleTogglePublish = async (faq) => {
    try {
      const token = getAdminToken();
      const newStatus = faq.status === 'published' ? 'draft' : 'published';

      const response = await fetch(ADMIN_API_ENDPOINTS.FAQS.BY_ID(faq.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ...faq, status: newStatus }),
      });

      if (response.ok) {
        fetchFaqs();
        showToast(
          newStatus === 'published' ? 'FAQ已发布' : 'FAQ已下架',
          'success'
        );
      } else {
        showToast('更新状态失败', 'error');
      }
    } catch (error) {
      console.error('Toggle publish error:', error);
      showToast('更新状态失败', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-gray-600 border-t-white rounded-full animate-spin"></div>
          <p className="mt-2 text-gray-400">加载中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 顶部操作栏 */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Q&A 管理</h2>
        <button
          onClick={handleCreate}
          className="px-6 py-2 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded-lg transition-colors"
        >
          + 新建FAQ
        </button>
      </div>

      {/* FAQ列表 */}
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={faq.id}
            className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-6 hover:border-[#4a5f3a] transition-colors"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block w-8 h-8 rounded-full bg-[#4a5f3a] text-white text-sm font-bold flex items-center justify-center">
                    {index + 1}
                  </span>
                  <h3 className="text-lg font-semibold text-white">
                    {faq.question?.zh || faq.question?.en || '(无标题)'}
                  </h3>
                  <span
                    className={`px-3 py-1 rounded-full text-xs ${faq.status === 'published'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-gray-500/20 text-gray-400'
                      }`}
                  >
                    {faq.status === 'published' ? '已发布' : '草稿'}
                  </span>
                </div>
                <p className="text-gray-400 text-sm">
                  {faq.answer?.zh || faq.answer?.en || '(无内容)'}
                </p>
                <div className="mt-2 text-xs text-gray-500">
                  排序: {faq.sort_order}
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(faq)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                >
                  编辑
                </button>
                <button
                  onClick={() => handleTogglePublish(faq)}
                  className={`px-4 py-2 text-white text-sm rounded transition-colors ${faq.status === 'published'
                    ? 'bg-orange-600 hover:bg-orange-700'
                    : 'bg-green-600 hover:bg-green-700'
                    }`}
                >
                  {faq.status === 'published' ? '下架' : '发布'}
                </button>
                <button
                  onClick={() => handleDelete(faq.id)}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded transition-colors"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        ))}

        {faqs.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            暂无FAQ，点击"新建FAQ"添加内容
          </div>
        )}
      </div>

      {/* 编辑对话框 */}
      {showDialog && editingFaq && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#1a1d17] border-b border-[#2a2d27] px-6 py-4">
              <h3 className="text-xl font-bold text-white">
                {editingFaq.id ? '编辑FAQ' : '新建FAQ'}
              </h3>
            </div>

            <div className="p-6 space-y-4">
              {/* 问题（中文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  问题（中文） *
                </label>
                <input
                  type="text"
                  value={editingFaq.question?.zh || ''}
                  onChange={(e) =>
                    setEditingFaq({
                      ...editingFaq,
                      question: { ...editingFaq.question, zh: e.target.value },
                    })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  placeholder="输入中文问题"
                />
              </div>

              {/* 问题（英文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  问题（英文） *
                </label>
                <input
                  type="text"
                  value={editingFaq.question?.en || ''}
                  onChange={(e) =>
                    setEditingFaq({
                      ...editingFaq,
                      question: { ...editingFaq.question, en: e.target.value },
                    })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                  placeholder="Enter question in English"
                />
              </div>

              {/* 答案（中文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  答案（中文） *
                </label>
                <textarea
                  value={editingFaq.answer?.zh || ''}
                  onChange={(e) =>
                    setEditingFaq({
                      ...editingFaq,
                      answer: { ...editingFaq.answer, zh: e.target.value },
                    })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a] min-h-[120px]"
                  placeholder="输入中文答案"
                />
              </div>

              {/* 答案（英文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  答案（英文） *
                </label>
                <textarea
                  value={editingFaq.answer?.en || ''}
                  onChange={(e) =>
                    setEditingFaq({
                      ...editingFaq,
                      answer: { ...editingFaq.answer, en: e.target.value },
                    })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a] min-h-[120px]"
                  placeholder="Enter answer in English"
                />
              </div>

              {/* 图片（中文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  配图（中文）- 可选
                </label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingFaq.image?.zh || ''}
                        onChange={(e) =>
                          setEditingFaq({
                            ...editingFaq,
                            image: { ...editingFaq.image, zh: e.target.value },
                          })
                        }
                        placeholder="输入图片URL或上传"
                        className="flex-1 px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                      />
                      <label className={`px-4 py-2 ${uploadingImage === 'zh' ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a7f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                        {uploadingImage === 'zh' ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            上传中...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="currentColor"
                              viewBox="0 0 256 256"
                            >
                              <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                            </svg>
                            上传
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'zh')}
                          className="hidden"
                          disabled={uploadingImage === 'zh'}
                        />
                      </label>
                    </div>
                    {editingFaq.image?.zh && (
                      <div className="mt-3 relative w-full h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                        <img
                          src={getImageUrl(editingFaq.image.zh)}
                          alt="中文配图预览"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src =
                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3E图片加载失败%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <button
                          onClick={() =>
                            setEditingFaq({
                              ...editingFaq,
                              image: { ...editingFaq.image, zh: '' },
                            })
                          }
                          className="absolute top-2 right-2 p-1 bg-red-600/80 text-white rounded-full hover:bg-red-700 transition-colors"
                          title="删除图片"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 图片（英文） */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  配图（英文）- 可选
                </label>
                <div className="flex gap-4 items-start">
                  <div className="flex-1">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingFaq.image?.en || ''}
                        onChange={(e) =>
                          setEditingFaq({
                            ...editingFaq,
                            image: { ...editingFaq.image, en: e.target.value },
                          })
                        }
                        placeholder="Enter image URL or upload"
                        className="flex-1 px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                      />
                      <label className={`px-4 py-2 ${uploadingImage === 'en' ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#4a5f3a] hover:bg-[#5a7f4a] cursor-pointer'} text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap`}>
                        {uploadingImage === 'en' ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            上传中...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              fill="currentColor"
                              viewBox="0 0 256 256"
                            >
                              <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                            </svg>
                            上传
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e, 'en')}
                          className="hidden"
                          disabled={uploadingImage === 'en'}
                        />
                      </label>
                    </div>
                    {editingFaq.image?.en && (
                      <div className="mt-3 relative w-full h-48 bg-[#1a1d17] border border-[#2a2d27] rounded-lg overflow-hidden">
                        <img
                          src={getImageUrl(editingFaq.image.en)}
                          alt="English Image Preview"
                          className="w-full h-full object-contain"
                          onError={(e) => {
                            e.target.src =
                              'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23333" width="200" height="200"/%3E%3Ctext fill="%23666" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EImage Load Failed%3C/text%3E%3C/svg%3E';
                          }}
                        />
                        <button
                          onClick={() =>
                            setEditingFaq({
                              ...editingFaq,
                              image: { ...editingFaq.image, en: '' },
                            })
                          }
                          className="absolute top-2 right-2 p-1 bg-red-600/80 text-white rounded-full hover:bg-red-700 transition-colors"
                          title="Delete Image"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            viewBox="0 0 256 256"
                          >
                            <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 排序 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  排序（数字越小越靠前）
                </label>
                <input
                  type="number"
                  value={editingFaq.sort_order}
                  onChange={(e) =>
                    setEditingFaq({
                      ...editingFaq,
                      sort_order: parseInt(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                />
              </div>

              {/* 状态 */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  状态
                </label>
                <select
                  value={editingFaq.status}
                  onChange={(e) =>
                    setEditingFaq({ ...editingFaq, status: e.target.value })
                  }
                  className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white focus:outline-none focus:border-[#5a6f4a]"
                >
                  <option value="published">已发布</option>
                  <option value="draft">草稿</option>
                </select>
              </div>
            </div>

            <div className="sticky bottom-0 bg-[#1a1d17] border-t border-[#2a2d27] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDialog(false);
                  setEditingFaq(null);
                }}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white rounded-lg transition-colors"
                disabled={
                  !editingFaq.question?.zh ||
                  !editingFaq.question?.en ||
                  !editingFaq.answer?.zh ||
                  !editingFaq.answer?.en
                }
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast提示 */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* 确认对话框 */}
      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          type={confirmDialog.type}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );
};

export default FAQManagement;
